export type Step = "validatingClaveUnicaLogin" | "ciDocForm" | "questionsForm";

export interface CIDocFormData {
  docNum: string;
  name: string;
  firstSurname: string;
  secondSurname: string;
  birthdate: string;
}

interface Option {
  optionId: string;
  optionText: string;
}

export interface Question {
  questionId: string;
  questionText: string;
  options: Option[];
}

interface ValidatedDoctorData {
  name: string;
  firstSurname: string;
  secondSurname: string;
  birthdate: string;
}
export interface ValidationChallengePayload {
  ciDocNum: string;
  validatedDoctorData?: ValidatedDoctorData;
}

export interface ValidationChallengeResponse {
  questions: Question[];
}

export enum BankTransactionStatus {
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
}

export interface GetBankTransactionChallengeResponse {
  paymentId: string;
  paymentUrl: string;
  simplifiedPaymentUrl: string;
}

export interface GetBankTransactionStatusResponse {
  bankTransferStatus: BankTransactionStatus;
  challengeStatus: BankTransactionStatus;
  validationId: string;
}

export interface PinState {
  pin: string;
  error: boolean;
  errorMessage: string;
}

export const defaultPinState: PinState = {
  pin: "",
  error: false,
  errorMessage: "",
};

export enum MekidocEsignError {
  PIN_INCORRECT = "PIN incorrecto",
  PIN_BLOCKED = "PIN bloqueado",
  UNKNOWN = "Ha ocurrido un error",
}

interface PinError {
  mekidocError: MekidocEsignError;
  esignError: string;
}
export function pinErrorMapper(errorMessage: string): PinError {
  let mekidocError: MekidocEsignError;
  if (!errorMessage) {
    mekidocError = MekidocEsignError.UNKNOWN;
  } else if (errorMessage.includes("Firma Bloqueada")) {
    mekidocError = MekidocEsignError.PIN_BLOCKED;
  } else {
    mekidocError = MekidocEsignError.PIN_INCORRECT;
  }
  return { mekidocError, esignError: errorMessage };
}

export function extractBlockingTime(errorMessage: string): string {
  const regex = /(\d+h:\d+m:\d+s)/;
  const match = errorMessage.match(regex);
  return match ? match[0] : "30 min";
}

export function parseBlockingTimeToSeconds(errorMessage: string): number {
  const regex = /(\d+)h:(\d+)m:(\d+)s/;
  const match = errorMessage.match(regex);

  if (match) {
    const hours = parseInt(match[1], 10);
    const minutes = parseInt(match[2], 10);
    const seconds = parseInt(match[3], 10);
    return hours * 3600 + minutes * 60 + seconds;
  }

  // Default fallback to 30 minutes if no match found
  return 30 * 60;
}

export function calculateUnblockTimestamp(errorMessage: string): number {
  const blockingTimeInSeconds = parseBlockingTimeToSeconds(errorMessage);
  return Date.now() + blockingTimeInSeconds * 1000;
}

export class InvalidPinEsignError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InvalidPinEsignError";
  }
}

export class BlockedSignatureEsignError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "BlockedSignatureEsignError";
  }
}

export class EsignError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "EsignError";
  }
}

export type QuestionsChallengeAction =
  | "block"
  | "invalidInput"
  | "retry"
  | "incompleteData";

export class QuestionsChallengeActionError extends Error {
  action: QuestionsChallengeAction;
  description?: string | React.ReactNode;
  code?: string;

  constructor(
    message: string,
    name: string,
    action: QuestionsChallengeAction,
    description?: string | React.ReactNode,
    code?: string,
  ) {
    super(message);
    this.name = name;
    this.action = action;
    this.description = description;
    this.code = code;
  }
}

export class UnableToGenerateQuestionsChallengesEsignError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "UnableToGenerateQuestionsChallengesEsignError";
  }
}

export class DocumentNumberDoesNotMatchEsignError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "DocumentNumberDoesNotMatchEsignError";
  }
}

export class ExceededMaxRetriesQuestionsChallengeEsignError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ExceededMaxRetriesQuestionsChallengeEsignError";
  }
}

export class IncompleteDoctorDataProvidedEsignError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "IncompleteDoctorDataProvidedEsignError";
  }
}

export class InvalidAnswersEsignError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "InvalidAnswersEsignError";
  }
}

export class QuestionChallengeNotAvailableEsignError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "QuestionChallengeNotAvailableEsignError";
  }
}
